import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db, createUserProfileDocument } from './firebase';
import { getAuthService } from './utils/enhancedAuth';
import { removeFCMToken } from './utils/fcmToken';
import { getSiteLogo } from './utils/siteLogo';

// Common Components (always needed)
import Navbar from './components/common/Navbar';
import Loader from './components/common/Loader';
import AuthModal from './components/auth/AuthModal';
import ThemeLoader from './components/common/ThemeLoader';
import ErrorBoundary from './components/common/ErrorBoundary';
import IOSFallback from './components/common/IOSFallback';
import VersionCheck from './components/common/VersionCheck';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './components/common/Toast';
import PWAManager from './components/common/PWAManager';
import PromoPopup from './components/common/PromoPopup';

// Eager-loaded (homepage is always first visit)
import HomePage from './components/pages/HomePage';

// Hooks
import { useGlobalNotifications } from './hooks/useGlobalNotifications';
import { useUnreadCounts } from './hooks/useUnreadCounts';

// Styles
import './App.css';
import './styles/ios-fixes.css';

// Lazy-loaded pages (only downloaded when visited)
const PortfolioPage = lazy(() => import('./components/pages/PortfolioPage'));
const NewsPage = lazy(() => import('./components/pages/NewsPage'));
const LiveStreamPage = lazy(() => import('./components/pages/LiveStreamPage'));
const OrderHistoryPage = lazy(() => import('./components/pages/OrderHistoryPage'));
const MarketplacePage = lazy(() => import('./components/pages/MarketplacePage'));
const MarketplaceDetailPage = lazy(() => import('./components/pages/MarketplaceDetailPage'));
const GameDetailPage = lazy(() => import('./components/pages/GameDetailPage'));
const GamesPage = lazy(() => import('./components/pages/GamesPage'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const JobsPage = lazy(() => import('./components/pages/JobsPage'));
const JobDetailPage = lazy(() => import('./components/pages/JobDetailPage'));
const MyJobsPage = lazy(() => import('./components/pages/MyJobsPage'));
const PartnersPage = lazy(() => import('./components/pages/PartnersPage'));

// Floating widgets — lazy-loaded so their JS (incl. the ~1.4k-LOC AI engine) stays out
// of the main bundle and streams in after first paint instead of blocking it.
const AIChatWidget = lazy(() => import('./components/ai/AIChatWidget'));
const ChatWidget = lazy(() => import('./components/chat/ChatWidget'));

// ScrollToTop component — scrolls to top on route change
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

// Protected Route wrapper
function ProtectedRoute({ user, children, fallback }) {
    if (!user || user.isAnonymous) {
        return fallback || <Navigate to="/" replace />;
    }
    return children;
}

// Admin Route wrapper
function AdminRoute({ userData, ready, children }) {
    // Wait until the user's role is known before deciding. The app shell now renders as
    // soon as auth is ready (not after the users-doc read), so without this guard a
    // logged-in admin refreshing on /admin would be redirected home before userData loads.
    if (!ready) {
        return <div className="min-h-screen flex items-center justify-center"><Loader /></div>;
    }
    if (userData?.role !== 'admin' && userData?.role !== 'superAdmin') {
        return <Navigate to="/" replace />;
    }
    return children;
}

function AppContent() {
    // iOS Detection
    const isIOS = () => {
        if (typeof navigator === 'undefined') return false;
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    };

    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [loading, setLoading] = useState(true);
    // Separate from `loading` — true once the users/{uid} snapshot has resolved at least once.
    // Used to delay ChatWidget render until we know the user's role (prevents admin flashing as user view).
    const [isUserDataReady, setIsUserDataReady] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [heroImageUrl, setHeroImageUrl] = useState(() => localStorage.getItem('heroImageUrl') || null);
    const [heroBrightness, setHeroBrightness] = useState(() => {
        const c = localStorage.getItem('heroBrightness');
        return c !== null ? Number(c) : 30;
    });
    const [overlayChatOpen, setOverlayChatOpen] = useState(false);
    const [forceChatOpen, setForceChatOpen] = useState(false);
    const [aiChatOpen, setAiChatOpen] = useState(false);
    const [adminChatOpen, setAdminChatOpen] = useState(false);

    // Initialize global notification listeners
    useGlobalNotifications(user, userData);

    // Real-time unread counts for nav badges
    const { userUnreadCount, adminUnreadCount } = useUnreadCounts(user, userData);

    // Dynamic favicon/logo from Firestore
    useEffect(() => {
        const fetchLogo = async () => {
            const logoUrl = await getSiteLogo();
            if (logoUrl) {
                document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach(link => {
                    link.href = logoUrl;
                });
            }
        };
        const fetchHeroImage = async () => {
            try {
                if (!db) {
                    const cached = localStorage.getItem('heroImageUrl');
                    if (cached) setHeroImageUrl(cached);
                    return;
                }
                const { getCachedDoc } = await import('./utils/docCache');
                const data = await getCachedDoc('public/data/site_settings/hero', 30 * 60 * 1000);
                if (data) {
                    if (data.backgroundImage) {
                        setHeroImageUrl(data.backgroundImage);
                        localStorage.setItem('heroImageUrl', data.backgroundImage);
                    }
                    if (data.brightness !== undefined) {
                        setHeroBrightness(data.brightness);
                        localStorage.setItem('heroBrightness', String(data.brightness));
                    }
                }
            } catch (err) {
                console.error('Error fetching hero image:', err);
                const cached = localStorage.getItem('heroImageUrl');
                if (cached) setHeroImageUrl(cached);
            }
        };
        fetchLogo();
        fetchHeroImage();
    }, []);

    // iOS-specific loading timeout and fallback
    const [showIOSFallback, setShowIOSFallback] = useState(false);

    // iOS specific error handling
    useEffect(() => {
        if (isIOS()) {
            console.log('iOS device detected - applying iOS-specific configurations');
            document.body.style.overscrollBehavior = 'none';
            const setViewportHeight = () => {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            };
            setViewportHeight();
            window.addEventListener('resize', setViewportHeight);
            window.addEventListener('orientationchange', setViewportHeight);
            return () => {
                window.removeEventListener('resize', setViewportHeight);
                window.removeEventListener('orientationchange', setViewportHeight);
            };
        }
    }, []);

    useEffect(() => {
        if (isIOS() && loading) {
            const timeout = setTimeout(() => {
                setShowIOSFallback(true);
                setLoading(false);
            }, 8000);
            return () => clearTimeout(timeout);
        }
    }, [loading]);

    const handleIOSRefresh = () => window.location.reload();
    const handleIOSClearData = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    // Tracks the previously signed-in uid across auth callbacks so we can drop this
    // device's FCM token when auth transitions to signed-out.
    const prevAuthUidRef = useRef(null);
    // uid whose FCM token was already removed via the logout button — lets the
    // onAuthStateChanged signed-out branch avoid removing the same token twice.
    const fcmRemovedUidRef = useRef(null);

    // Auth state
    useEffect(() => {
        const handleAuth = async () => {
            try {
                await getAuthService().handleRedirectResult();
            } catch (error) {
                console.error('Auth redirect error:', error);
                if (isIOS()) setLoading(false);
            }
        };
        handleAuth();

        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            try {
                const prevUid = prevAuthUidRef.current;
                prevAuthUidRef.current = user?.uid || null;

                setUser(user);
                setIsAuthReady(true);
                if (!user) {
                    setUserData(null);
                    // Auth transitioned to signed-out: drop this device's FCM token so the
                    // next person to log in here doesn't inherit the previous user's pushes.
                    // Skip if the logout button already removed it for this uid.
                    if (prevUid && fcmRemovedUidRef.current !== prevUid) {
                        removeFCMToken(prevUid).catch(() => {});
                    }
                }
                // Render the app shell as soon as auth is known — do NOT wait for the
                // users/{uid} read. Role-specific UI is gated separately by isUserDataReady,
                // so blocking the whole app (incl. the public homepage) on that read just
                // made every logged-in visit slow and widened status-race windows.
                setLoading(false);
            } catch (error) {
                console.error('Auth state change error:', error);
                if (isIOS()) { setLoading(false); setIsAuthReady(true); }
            }
        });
        return () => unsubscribeAuth();
    }, []);

    // User data listener
    const prevUidRef = useRef(null);
    useEffect(() => {
        let unsubscribeUser = null;
        // Only reset ready flag when uid actually changes (login/logout/switch user),
        // not on every auth-object refresh — otherwise ChatWidget unmounts mid-session
        // and the button "disappears" until the next snapshot fires.
        const currentUid = user?.uid || null;
        if (prevUidRef.current !== currentUid) {
            setIsUserDataReady(false);
            prevUidRef.current = currentUid;
        }
        if (isAuthReady && user) {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                unsubscribeUser = onSnapshot(userDocRef, (doc) => {
                    try {
                        if (doc.exists()) {
                            setUserData({ uid: doc.id, ...doc.data() });
                            // storage.rules trusts the ID-token `role` claim, not Firestore
                            // (it can't read Firestore). The syncAdminClaim CF sets that claim,
                            // but an already-signed-in admin keeps a stale token for up to an
                            // hour. If Firestore says admin and the token doesn't yet, force a
                            // one-time refresh so admin image uploads work immediately.
                            const role = doc.data().role;
                            if (role === 'admin' || role === 'superAdmin') {
                                user.getIdTokenResult().then((res) => {
                                    if (res.claims.role !== role) return user.getIdToken(true);
                                }).catch(() => {});
                            }
                        } else {
                            // Skip anonymous visitors — creating a users/{uid} profile for a
                            // guest would inflate stats.totalUsers on every anonymous sign-in.
                            if (!user.isAnonymous) {
                                createUserProfileDocument(user).catch(err => console.error('profile create failed', err));
                            }
                            setUserData(null);
                        }
                        setLoading(false);
                        setIsUserDataReady(true); // role is now known — show ChatWidget
                    } catch (docError) {
                        console.error("Error processing user document:", docError);
                        if (isIOS()) setUserData({ uid: user.uid, email: user.email, displayName: user.displayName });
                        setLoading(false);
                        setIsUserDataReady(true);
                    }
                }, (error) => {
                    console.error("Error fetching user data:", error);
                    if (isIOS()) setUserData({ uid: user.uid, email: user.email, displayName: user.displayName });
                    setLoading(false);
                    setIsUserDataReady(true);
                });
            } catch (setupError) {
                console.error("Error setting up user data listener:", setupError);
                if (isIOS()) setUserData({ uid: user.uid, email: user.email, displayName: user.displayName });
                setLoading(false);
                setIsUserDataReady(true);
            }
        } else {
            setLoading(false);
            setIsUserDataReady(false); // logged out — hide ChatWidget
        }
        return () => { if (unsubscribeUser) unsubscribeUser(); };
    }, [user, isAuthReady]);

    // Load global theme
    useEffect(() => {
        const themes = {
            default: { primary: '#00ff88', secondary: '#ff4757', accent1: '#ffa502', accent2: '#3742fa' },
            neon: { primary: '#00d4ff', secondary: '#ff00ff', accent1: '#ffff00', accent2: '#00ff00' },
            cyberpunk: { primary: '#ff073a', secondary: '#39ff14', accent1: '#ffff41', accent2: '#0abdc6' },
            ocean: { primary: '#0099ff', secondary: '#00ffcc', accent1: '#66ccff', accent2: '#0066cc' },
            sunset: { primary: '#ff6b35', secondary: '#f7931e', accent1: '#ffb347', accent2: '#ff4757' },
            forest: { primary: '#2ecc71', secondary: '#27ae60', accent1: '#52c41a', accent2: '#95de64' },
            galaxy: { primary: '#8b5cf6', secondary: '#a855f7', accent1: '#c084fc', accent2: '#e879f9' },
            fire: { primary: '#e74c3c', secondary: '#f39c12', accent1: '#e67e22', accent2: '#d35400' },
            ice: { primary: '#3498db', secondary: '#00bcd4', accent1: '#81d4fa', accent2: '#4fc3f7' },
            royal: { primary: '#9b59b6', secondary: '#8e44ad', accent1: '#af7ac5', accent2: '#d2b4de' },
            matrix: { primary: '#00ff41', secondary: '#008f11', accent1: '#39ff14', accent2: '#00c851' },
            toxic: { primary: '#76ff03', secondary: '#cddc39', accent1: '#ffeb3b', accent2: '#8bc34a' },
            space: { primary: '#1e3a8a', secondary: '#3730a3', accent1: '#6366f1', accent2: '#8b5cf6' },
            blood: { primary: '#dc2626', secondary: '#991b1b', accent1: '#ef4444', accent2: '#f87171' },
            gold: { primary: '#fbbf24', secondary: '#f59e0b', accent1: '#fcd34d', accent2: '#fed7aa' },
            midnight: { primary: '#1f2937', secondary: '#374151', accent1: '#6b7280', accent2: '#9ca3af' },
            aurora: { primary: '#06ffa5', secondary: '#00d4aa', accent1: '#36f5f7', accent2: '#9b59b6' },
            cherry: { primary: '#ffc0cb', secondary: '#ff69b4', accent1: '#ffb6c1', accent2: '#dda0dd' },
            volcano: { primary: '#ff4500', secondary: '#dc143c', accent1: '#ff6347', accent2: '#ff8c00' },
            aqua: { primary: '#00ffff', secondary: '#20b2aa', accent1: '#48d1cc', accent2: '#7fffd4' }
        };

        const applyThemeToDOM = (theme) => {
            try {
                if (!theme || typeof theme !== 'object') theme = themes.default;
                const requiredProps = ['primary', 'secondary', 'accent1', 'accent2'];
                for (const prop of requiredProps) {
                    if (!theme[prop] || typeof theme[prop] !== 'string') { theme = themes.default; break; }
                }
                const root = document.documentElement;
                if (!root) return;
                root.style.setProperty('--gaming-primary', theme.primary);
                root.style.setProperty('--gaming-secondary', theme.secondary);
                root.style.setProperty('--gaming-accent-1', theme.accent1);
                root.style.setProperty('--gaming-accent-2', theme.accent2);
                root.style.setProperty('--gaming-gradient-primary', `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent2} 100%)`);
                root.style.setProperty('--gaming-gradient-secondary', `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.accent1} 100%)`);
                root.style.setProperty('--gaming-gradient-neon', `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 50%, ${theme.secondary} 100%)`);
            } catch (error) {
                console.error("Error applying theme to DOM:", error);
            }
        };

        applyThemeToDOM(themes.default);

        // Theme — one-time cached read (was a live listener opened by EVERY visitor for a
        // rarely-changing settings doc; a top contributor to the concurrent-listener count).
        // Apply from localStorage instantly; while the cache is fresh, skip the read entirely.
        try {
            const LS_KEY = 'fl_global_theme_v1';
            const TTL = 30 * 60 * 1000; // 30 minutes
            const applyByName = (name) => applyThemeToDOM(themes[name] || themes['default']);
            let fresh = false;
            try {
                const raw = localStorage.getItem(LS_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    applyByName(parsed.theme);
                    fresh = (Date.now() - parsed.ts) < TTL;
                }
            } catch (_) { }
            if (!fresh) {
                getDoc(doc(db, "settings", "globalTheme")).then((docSnap) => {
                    const themeName = docSnap.exists() ? (docSnap.data().theme || 'default') : 'default';
                    try { localStorage.setItem(LS_KEY, JSON.stringify({ theme: themeName, ts: Date.now() })); } catch (_) { }
                    applyByName(themeName);
                }).catch(() => applyThemeToDOM(themes.default));
            }
        } catch (error) {
            applyThemeToDOM(themes.default);
        }
    }, []);

    const handleLoginClick = () => setShowAuthModal(true);
    const handleLogoutClick = async () => {
        try {
            // Remove FCM token from Firestore BEFORE signOut so we still have credentials to write.
            // Prevents this device from receiving push notifications meant for the next user
            // who logs in here.
            if (user?.uid) {
                // Record that this uid's token is being cleaned up here so the
                // onAuthStateChanged signed-out branch doesn't remove it a second time.
                fcmRemovedUidRef.current = user.uid;
                await removeFCMToken(user.uid).catch(err => console.warn('FCM cleanup failed:', err));
            }
            await signOut(auth);
            setUser(null);
            setUserData(null);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };
    const handleAuthSuccess = useCallback(() => {
        if (isIOS()) {
            setTimeout(() => setShowAuthModal(false), 500);
        } else {
            setShowAuthModal(false);
        }
    }, []);
    const handleCloseAuthModal = useCallback(() => setShowAuthModal(false), []);

    if (loading) {
        return (
            <div className="App" style={{ backgroundColor: '#0F172A', minHeight: '100vh' }}>
                <div className="flex flex-col justify-center items-center h-screen gap-6">
                    <div style={{
                        animation: 'brandPulse 2s ease-in-out infinite',
                    }}>
                        <div
                            className="w-20 h-20 rounded-2xl flex items-center justify-center"
                            style={{
                                background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
                                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
                            }}
                        >
                            <span className="text-3xl">🎮</span>
                        </div>
                    </div>
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            Fastlane<span style={{ color: '#818CF8' }}>Lv.</span>
                        </h2>
                        <div className="flex items-center gap-2 justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400" style={{ animation: 'loadDot 1.4s ease-in-out infinite' }} />
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" style={{ animation: 'loadDot 1.4s ease-in-out 0.2s infinite' }} />
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400" style={{ animation: 'loadDot 1.4s ease-in-out 0.4s infinite' }} />
                        </div>
                    </div>
                    <style>{`
                        @keyframes brandPulse {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.08); }
                        }
                        @keyframes loadDot {
                            0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                            40% { opacity: 1; transform: scale(1.2); }
                        }
                    `}</style>
                </div>
            </div>
        );
    }

    if (isIOS() && showIOSFallback) {
        return (
            <IOSFallback
                user={user}
                userData={userData}
                onRefresh={handleIOSRefresh}
                onClearData={handleIOSClearData}
            />
        );
    }

    return (
        <ErrorBoundary>
            <div className="App gaming-bg">
                <PromoPopup />
                <PWAManager />
                <VersionCheck />
                <ThemeLoader />
                <ScrollToTop />
                <Navbar
                    user={user}
                    userData={userData}
                    onLoginClick={handleLoginClick}
                    onLogoutClick={handleLogoutClick}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                    userUnreadCount={userUnreadCount}
                    adminUnreadCount={adminUnreadCount}
                />
                <main className={`transition-all duration-100 ${mobileMenuOpen ? 'blur-sm brightness-50' : ''}`}>
                    {/* Inner ErrorBoundary so a route crash doesn't tear down Navbar/ChatWidget */}
                    <ErrorBoundary>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader /></div>}>
                        <Routes>
                            <Route path="/" element={
                                <HomePage user={user} userData={userData} isAuthReady={isAuthReady} onLoginClick={handleLoginClick} mobileMenuOpen={mobileMenuOpen} heroImageUrl={heroImageUrl} heroBrightness={heroBrightness} />
                            } />
                            <Route path="/portfolio" element={<PortfolioPage isAuthReady={isAuthReady} />} />
                            <Route path="/news" element={<NewsPage />} />
                            <Route path="/live" element={<LiveStreamPage />} />
                            <Route path="/marketplace" element={
                                <MarketplacePage user={user} userData={userData} isAuthReady={isAuthReady} onLoginClick={handleLoginClick} />
                            } />
                            <Route path="/marketplace/:id" element={
                                <MarketplaceDetailPage user={user} userData={userData} isAuthReady={isAuthReady} onLoginClick={handleLoginClick} />
                            } />
                            <Route path="/game/:gameId" element={
                                <GameDetailPage user={user} isAuthReady={isAuthReady} onLoginClick={handleLoginClick} mobileMenuOpen={mobileMenuOpen} />
                            } />
                            <Route path="/games" element={
                                <GamesPage user={user} isAuthReady={isAuthReady} onLoginClick={handleLoginClick} mobileMenuOpen={mobileMenuOpen} />
                            } />
                            <Route path="/history" element={
                                <ProtectedRoute user={user}>
                                    <OrderHistoryPage user={user} isAuthReady={isAuthReady} onChatOpenChange={setOverlayChatOpen} mobileMenuOpen={mobileMenuOpen} />
                                </ProtectedRoute>
                            } />
                            <Route path="/admin" element={
                                <AdminRoute userData={userData} ready={isAuthReady && (!user || (isUserDataReady && userData?.uid === user?.uid))}>
                                    <AdminDashboard user={user} userData={userData} isAuthReady={isAuthReady} onChatOpenChange={setOverlayChatOpen} />
                                </AdminRoute>
                            } />
                            {/* Jobboard — /jobs and /jobs/:id are public (guests browse); /jobs/mine needs a real account.
                                /jobs/mine is declared before /jobs/:id so "mine" is never read as a job id. */}
                            <Route path="/jobs" element={
                                <JobsPage user={user} userData={userData} isAuthReady={isAuthReady} isUserDataReady={isUserDataReady} onLoginClick={handleLoginClick} />
                            } />
                            <Route path="/jobs/mine" element={
                                <ProtectedRoute user={user}>
                                    <MyJobsPage user={user} userData={userData} isAuthReady={isAuthReady} isUserDataReady={isUserDataReady} onLoginClick={handleLoginClick} />
                                </ProtectedRoute>
                            } />
                            <Route path="/jobs/:id" element={
                                <JobDetailPage user={user} userData={userData} isAuthReady={isAuthReady} isUserDataReady={isUserDataReady} onLoginClick={handleLoginClick} />
                            } />
                            <Route path="/partners" element={<PartnersPage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                    </ErrorBoundary>
                </main>

                {/* Floating widgets are lazy — wrap in Suspense so their chunks can load
                    without a fallback (they're non-blocking overlays). */}
                <Suspense fallback={null}>
                    {isAuthReady && user && isUserDataReady && <ChatWidget user={user} userData={userData} mobileMenuOpen={mobileMenuOpen} overlayChatOpen={overlayChatOpen} forceChatOpen={forceChatOpen} onForceChatHandled={() => setForceChatOpen(false)} otherChatOpen={aiChatOpen} onOpenChange={setAdminChatOpen} />}

                    {/* AI Chat Widget — replaces per-page LiveChat */}
                    <AIChatWidget user={user} onLoginClick={handleLoginClick} mobileMenuOpen={mobileMenuOpen} onContactAdmin={() => setForceChatOpen(true)} otherChatOpen={adminChatOpen || overlayChatOpen} onOpenChange={setAiChatOpen} />
                </Suspense>

                {showAuthModal && (
                    <AuthModal
                        onClose={handleCloseAuthModal}
                        onAuthSuccess={handleAuthSuccess}
                        heroImageUrl={heroImageUrl}
                    />
                )}
            </div>
        </ErrorBoundary>
    );
}

function App() {
    return (
        <LanguageProvider>
            <ToastProvider>
                <Router>
                    <AppContent />
                </Router>
            </ToastProvider>
        </LanguageProvider>
    );
}

export default App;
